import styled from "styled-components";

export const ApprovalHistoryWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.border.light};
  padding: 2rem;

  @media screen and (max-width: ${({ theme }) => theme.screen.mobile}) {
    padding: 2rem 0.5rem;
  }

  .panel-header {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    .panel-title {
      color: ${({ theme }) => theme.text.primary};
      font-size: 1.1rem;
      font-weight: 600;
    }

    .panel-subtitle {
      color: ${({ theme }) => theme.text.prim_100};
      font-size: 0.8rem;
      font-weight: 400;
    }
  }
`;

export const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .headline {
    color: ${({ theme }) => theme.text.primary};
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5;
    padding: 1rem;
    border-radius: 0.25rem;
    border-left: 4px solid ${({ theme }) => theme.bg.secondary};
    background-color: ${({ theme }) => theme.bg.prim_400};

    @media screen and (max-width: ${({ theme }) => theme.screen.mobile}) {
      font-size: 0.9rem;
    }
  }

  .flag {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.85rem 1rem;
    border-radius: 0.25rem;
    border: 1px solid ${({ theme }) => theme.text.danger};
    background-color: ${({ theme }) => theme.text.danger}12;

    .flag-icon {
      color: ${({ theme }) => theme.text.danger};
      font-size: 1.1rem;
      flex-shrink: 0;
    }

    .flag-text {
      color: ${({ theme }) => theme.text.danger};
      font-size: 0.8rem;
      font-weight: 500;
      line-height: 1.5;
    }
  }

  .counts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: 0.75rem;

    .count-tile {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      padding: 0.75rem;
      border: 1px solid ${({ theme }) => theme.border.light};
      border-radius: 0.25rem;

      .count-value {
        color: ${({ theme }) => theme.text.primary};
        font-size: 1.3rem;
        font-weight: 600;
      }

      .count-label {
        color: ${({ theme }) => theme.text.prim_100};
        font-size: 0.75rem;
        font-weight: 400;
      }
    }
  }

  .last-decision {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    color: ${({ theme }) => theme.text.prim_100};
    font-size: 0.8rem;

    .decision-label {
      font-weight: 500;
    }
  }
`;

export const TimelineWrapper = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
`;

export const TimelineItem = styled.li<{ colorKey: string }>`
  display: flex;
  gap: 1rem;
  padding: 0 0 1.5rem;
  position: relative;

  :last-child {
    padding-bottom: 0;

    .track .line {
      display: none;
    }
  }

  .track {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;

    .dot {
      width: 0.75rem;
      height: 0.75rem;
      margin-top: 0.3rem;
      border-radius: 50%;
      background-color: ${({ theme, colorKey }) => theme.text[colorKey]};
    }

    .line {
      flex-grow: 1;
      width: 1px;
      margin-top: 0.35rem;
      background-color: ${({ theme }) => theme.border.light};
    }
  }

  .event {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    flex-grow: 1;
    padding-bottom: 0.5rem;

    .event-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 1rem;
      flex-wrap: wrap;

      .event-label {
        color: ${({ theme, colorKey }) => theme.text[colorKey]};
        font-size: 0.9rem;
        font-weight: 600;
      }

      .event-date {
        color: ${({ theme }) => theme.text.prim_200};
        font-size: 0.75rem;
        font-weight: 400;
      }
    }

    .event-actor {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      color: ${({ theme }) => theme.text.prim_100};
      font-size: 0.8rem;
      font-weight: 400;

      /* The accountable party — the submitting company, or the deciding
         administrator where there is no company. */
      .actor-primary {
        font-weight: 500;
      }

      /* The person who acted for that company. Audit detail rather than the
         headline answer, so it is smaller and quieter. */
      .actor-secondary {
        color: ${({ theme }) => theme.text.prim_200};
        font-size: 0.72rem;
        font-weight: 400;
      }
    }

    .event-remark {
      color: ${({ theme }) => theme.text.prim_50};
      font-size: 0.8rem;
      font-weight: 400;
      line-height: 1.5;
      padding: 0.6rem 0.75rem;
      border-radius: 0.25rem;
      background-color: ${({ theme }) => theme.bg.tert_400};

      .remark-label {
        font-weight: 500;
      }
    }

    .event-note {
      color: ${({ theme }) => theme.text.prim_200};
      font-size: 0.7rem;
      font-style: italic;
    }

    /* MED-678: an event whose acting user was never recorded. Muted, so it
       reads as missing information rather than as a named actor. */
    .actor-unattributed {
      font-style: italic;
      color: ${({ theme }) => theme.text.prim_200};
    }
  }
`;

/* `highlight` marks the comparison against the last approved version — the diff
   the admin is actually approving, as opposed to the single edit below it. */
export const ChangesWrapper = styled.div<{ highlight?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
  padding: 0.75rem;
  border: 1px solid
    ${({ theme, highlight }) =>
      highlight ? theme.bg.secondary : theme.border.light};
  border-radius: 0.25rem;
  background-color: ${({ theme, highlight }) =>
    highlight ? `${theme.bg.prim_400}` : "transparent"};

  .changes-title {
    color: ${({ theme, highlight }) =>
      highlight ? theme.text.primary : theme.text.prim_100};
    font-size: 0.75rem;
    font-weight: ${({ highlight }) => (highlight ? 600 : 500)};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .changes-subtitle {
    color: ${({ theme }) => theme.text.prim_200};
    font-size: 0.7rem;
    font-weight: 400;
    margin-top: -0.25rem;
  }

  .change-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.4rem;
    font-size: 0.8rem;

    .change-field {
      color: ${({ theme }) => theme.text.prim_100};
      font-weight: 500;
      min-width: 9rem;
    }

    .change-from {
      color: ${({ theme }) => theme.text.prim_200};
      text-decoration: line-through;
      word-break: break-word;
    }

    .change-arrow {
      color: ${({ theme }) => theme.text.prim_200};
    }

    .change-to {
      color: ${({ theme }) => theme.text.primary};
      font-weight: 500;
      word-break: break-word;
    }

    @media screen and (max-width: ${({ theme }) => theme.screen.mobile}) {
      .change-field {
        min-width: 100%;
      }
    }
  }
`;
